import { useEffect, useRef, useState } from "react";
import { getEventTargetMs, formatEventLabel, splitDuration } from "../../lib/eventTime";

/**
 * Countdown to the event.
 * - Uses an explicit timezone-aware target (see /lib/eventTime.js)
 * - Self-correcting tick (aligns to the next real second, so no drift and
 *   no frozen numbers when the tab regains focus).
 * - Also re-syncs on `visibilitychange` (fixes throttled background tabs).
 */
export default function Countdown() {
  const targetRef = useRef(getEventTargetMs());
  const [t, setT] = useState(() => splitDuration(targetRef.current - Date.now()));

  useEffect(() => {
    let timeoutId;
    const tick = () => {
      const remaining = targetRef.current - Date.now();
      setT(splitDuration(remaining));
      // Align next tick to the next second boundary to avoid drift.
      const nextIn = 1000 - (Date.now() % 1000);
      timeoutId = setTimeout(tick, nextIn);
    };
    tick();

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        clearTimeout(timeoutId);
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const items = [
    { label: "Jours", value: t.days },
    { label: "Heures", value: t.hours },
    { label: "Min", value: t.minutes },
    { label: "Sec", value: t.seconds },
  ];

  const eventLabel = formatEventLabel();

  return (
    <div data-testid="countdown" className="flex flex-col gap-2">
      <div
        data-testid="countdown-grid"
        className="flex items-stretch gap-px border border-white/10 bg-white/[0.03]"
      >
        {items.map((it) => (
          <div
            key={it.label}
            data-testid={`countdown-${it.label.toLowerCase()}`}
            className="flex min-w-[64px] flex-1 flex-col items-center px-4 py-3 lg:px-6 lg:py-4"
          >
            <span className="font-display text-3xl leading-none text-white lg:text-4xl tabular-nums">
              {String(it.value).padStart(2, "0")}
            </span>
            <span className="mt-1 font-mono-x text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              {it.label}
            </span>
          </div>
        ))}
      </div>
      {eventLabel && (
        <span
          data-testid="countdown-target-label"
          className="font-mono-x text-[10px] uppercase tracking-[0.25em] text-zinc-500"
        >
          {eventLabel}
        </span>
      )}
    </div>
  );
}
